<template>
    <div class="catalog-page-container py-4">
        <!-- Hero Section -->
        <div class="hero-banner mb-5 p-4 p-md-5 rounded-3xl position-relative overflow-hidden">
            <div class="hero-glow-bg position-absolute"></div>
            <div class="row align-items-center position-relative" style="z-index: 2;">
                <div class="col-lg-7 col-md-12 mb-4 mb-lg-0 text-left">
                    <h1 class="hero-title font-weight-bold mb-3">
                        Descubra Nossos Produtos
                    </h1>
                    <p class="hero-subtitle mb-0">
                        Explore nosso catálogo exclusivo feito com carinho.
                    </p>
                </div>
                <div class="col-lg-5 col-md-12">
                    <div class="search-box-wrapper position-relative">
                        <input 
                            v-model="pesquisa"
                            type="text" 
                            class="form-control catalog-search-input shadow-sm" 
                            placeholder="O que você está procurando?">
                        <button 
                            v-if="pesquisa" 
                            key="btn-clear"
                            @click="pesquisa = ''" 
                            class="btn-clear-search position-absolute"
                            title="Limpar busca">
                            <i class="fas fa-times"></i>
                        </button>
                        <i v-else key="icon-search" class="fas fa-search position-absolute search-icon"></i>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main Layout Row: Sidebar Left + Content Right -->
        <div class="row align-items-start">
            <!-- Left Sidebar: Categories Navigation -->
            <div class="col-lg-3 col-md-4 mb-4" v-if="categorias.length > 0" key="sidebar-left">
                <div class="categories-sidebar p-4 rounded-3xl bg-surface-theme border-theme shadow-sm sticky-top" style="top: 90px; z-index: 10;">
                    <div class="d-flex align-items-center justify-content-between mb-3 pb-3 border-bottom-sidebar">
                        <h5 class="sidebar-title font-weight-bold mb-0">
                            <i class="fas fa-layer-group mr-2 text-accent"></i> Categorias
                        </h5>
                        <button 
                            v-if="categoriaSelecionada !== '' || pesquisa !== ''"
                            key="btn-reset-sidebar"
                            @click="resetFiltros" 
                            class="btn-reset-sidebar text-secondary-theme font-weight-bold p-0 text-decoration-none small"
                            title="Limpar Filtros">
                            <i class="fas fa-undo-alt mr-1"></i> Limpar
                        </button>
                    </div>

                    <!-- Vertical List of Categories -->
                    <div class="category-vertical-list d-flex flex-column">
                        <button 
                            class="sidebar-cat-item mb-2" 
                            :class="{ 'active': categoriaSelecionada === '' }"
                            @click="selecionarCategoria('')">
                            <i class="fas fa-border-all mr-3 cat-icon"></i> 
                            <span class="text-left flex-grow-1">Todos</span>
                        </button>
                        <button 
                            v-for="categoria in categorias" 
                            :key="'cat-' + categoria.id"
                            class="sidebar-cat-item mb-2" 
                            :class="{ 'active': categoriaSelecionada === categoria.id }"
                            @click="selecionarCategoria(categoria.id)">
                            <i class="fas fa-tag mr-3 cat-icon"></i> 
                            <span class="text-left flex-grow-1">{{ categoria.nome }}</span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Right Column: Product Grid & Pagination -->
            <div :class="categorias.length > 0 ? 'col-lg-9 col-md-8' : 'col-12'" key="products-right-col">
                <!-- Result Counter & Search Pill -->
                <div class="d-flex align-items-center justify-content-between mb-3 px-1 flex-wrap" v-if="pagination.total !== undefined">
                    <span class="text-secondary-theme small font-weight-bold">
                        Exibindo <span class="accent-color">{{ pagination.total }}</span> {{ pagination.total === 1 ? 'produto' : 'produtos' }}
                    </span>
                    <span v-if="pesquisa" class="badge badge-pill badge-search-info px-3 py-2 mt-1 mt-sm-0">
                        <i class="fas fa-search mr-1"></i> "{{ pesquisa }}"
                    </span>
                </div>

                <!-- Product Grid Container with Loading Overlay -->
                <div class="position-relative min-h-400">
                    <!-- Loading Spinner Overlay -->
                    <div v-if="isCalculating" key="loading-overlay" class="loading-overlay d-flex justify-content-center align-items-center position-absolute w-100 h-100">
                        <div class="spinner-box p-4 rounded-circle shadow-lg bg-surface-theme d-flex align-items-center justify-content-center">
                            <div class="spinner-border text-accent" role="status" style="width: 2.8rem; height: 2.8rem;">
                                <span class="sr-only">Carregando...</span>
                            </div>
                        </div>
                    </div>

                    <!-- Product Grid Component -->
                    <produto-card :dados="dados"></produto-card>
                </div>

                <!-- Custom Floating Pagination -->
                <div class="d-flex flex-column align-items-center mt-5 mb-4" v-if="pagination.last_page > 1" key="pagination-box">
                    <div class="pagination-info mb-2 small text-secondary-theme font-weight-bold">
                        Página {{ pagination.current_page }} de {{ pagination.last_page }}
                    </div>
                    <nav aria-label="Navegação de página">
                        <ul class="pagination custom-pagination mb-0 flex-wrap justify-content-center">
                            <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                                <button class="page-link" @click.prevent="getDados(pagination.current_page - 1)" :disabled="pagination.current_page === 1" aria-label="Anterior">
                                    <i class="fas fa-chevron-left"></i>
                                </button>
                            </li>
                            
                            <li class="page-item" v-for="page in pagination.last_page" :key="'page-' + page" :class="{ active: pagination.current_page === page }">
                                <button class="page-link" @click.prevent="getDados(page)">{{ page }}</button>
                            </li>

                            <li class="page-item" :class="{ disabled: pagination.current_page === pagination.last_page }">
                                <button class="page-link" @click.prevent="getDados(pagination.current_page + 1)" :disabled="pagination.current_page === pagination.last_page" aria-label="Próximo">
                                    <i class="fas fa-chevron-right"></i>
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        data(){
            return {
                pesquisa: '',
                searchQueryIsDirty: false,
                isCalculating: false,
                dados: [],
                link: this.$url+'/api/produtos/data',
                pagination: {},
                debounceTimeout: null,
                categorias: [],
                categoriaSelecionada: '',
            }
        },
        created(){
            window.scrollTo(0, 0);
            this.getDados();
            this.getCategorias();
        },
        mounted(){
            window.scrollTo(0, 0);
        },
        methods: {
            getDados(page=''){
                this.isCalculating = true;
                axios.get(this.getComplexLink(page))
	            .then(response => {
	            	this.dados = response.data.data.data
                    this.pagination = response.data.data
                    this.isCalculating = false;
	            })
	            .catch(error => {
	            	console.log(error)
                    this.isCalculating = false;
	            });
            },
            getComplexLink(page){
                var complexLink = '';
                if(this.link.indexOf("?") == -1){
                    complexLink = this.link+'?page='+page;
                } else {
                    complexLink = this.link+'&page='+page;
                }
                if(this.pesquisa){
                    complexLink += '&pesquisa='+this.pesquisa;
                }
                if(this.categoriaSelecionada !== ''){
                    complexLink += '&categoria_id='+this.categoriaSelecionada;
                }
                return complexLink;
            },
            selecionarCategoria(id) {
                this.categoriaSelecionada = id;
                this.getDados(1);
            },
            resetFiltros() {
                this.categoriaSelecionada = '';
                this.pesquisa = '';
                this.getDados(1);
            },
            getCategorias() {
                axios.get(this.$url+'/api/produtos/categorias')
                .then(response => {
                    this.categorias = response.data;
                })
                .catch(error => {
                    console.log(error);
                });
            },
            expensiveOperation(){
                this.isCalculating = true;
                if (this.debounceTimeout) {
                    clearTimeout(this.debounceTimeout);
                }
                this.debounceTimeout = setTimeout(function () {
                    this.searchQueryIsDirty = false
                    this.getDados();
                }.bind(this), 600)
            }
        },
        watch: {
            pesquisa: function () {
                if(this.pesquisa){
                    this.searchQueryIsDirty = true
                    this.expensiveOperation()
                } else {
                    if (this.debounceTimeout) {
                        clearTimeout(this.debounceTimeout);
                    }
                    this.isCalculating = false;
                    this.getDados()
                }
            }
        }
    }
</script>

<style scoped>
    .catalog-page-container {
        max-width: 1280px;
        margin: 0 auto;
    }

    /* Hero Banner */
    .hero-banner {
        background: linear-gradient(135deg, rgba(145, 216, 247, 0.15) 0%, rgba(247, 173, 175, 0.15) 100%);
        border: 1px solid var(--border-color);
        border-radius: 28px !important;
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
    }

    :root[data-theme="dark"] .hero-banner {
        background: linear-gradient(135deg, rgba(45, 55, 72, 0.8) 0%, rgba(26, 32, 44, 0.9) 100%);
    }

    .hero-glow-bg {
        width: 350px;
        height: 350px;
        background: radial-gradient(circle, var(--accent-color) 0%, rgba(255,255,255,0) 70%);
        opacity: 0.18;
        top: -100px;
        right: -50px;
        pointer-events: none;
        border-radius: 50%;
    }

    .badge-hero {
        background-color: var(--surface-color);
        color: var(--text-primary);
        font-size: 0.82rem;
        font-weight: 700;
        border: 1px solid var(--border-color);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    }

    .hero-title {
        font-size: 2.8rem;
        letter-spacing: -1.2px;
        color: var(--text-primary);
        line-height: 1.15;
    }

    .hero-subtitle {
        font-size: 1.08rem;
        color: var(--text-secondary);
        max-width: 580px;
        line-height: 1.6;
    }

    /* Search Box */
    .search-box-wrapper {
        width: 100%;
    }

    .catalog-search-input {
        background-color: var(--surface-color);
        border: 1px solid var(--border-color);
        color: var(--text-primary);
        border-radius: 50px;
        padding: 0.9rem 3.5rem 0.9rem 1.6rem;
        font-size: 1rem;
        height: 56px;
        transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
    }

    .catalog-search-input:focus {
        outline: none;
        border-color: var(--accent-color);
        box-shadow: 0 0 0 4px rgba(145, 216, 247, 0.25) !important;
        background-color: var(--surface-color);
        color: var(--text-primary);
    }

    .search-icon {
        right: 22px;
        top: 50%;
        transform: translateY(-50%);
        color: var(--text-secondary);
        font-size: 1.1rem;
        pointer-events: none;
    }

    .btn-clear-search {
        right: 18px;
        top: 50%;
        transform: translateY(-50%);
        background: transparent;
        border: none;
        color: var(--text-secondary);
        font-size: 1.1rem;
        cursor: pointer;
        padding: 4px 8px;
        border-radius: 50%;
        transition: color 0.2s ease;
    }

    .btn-clear-search:hover {
        color: var(--text-primary);
    }

    /* Sidebar Categories Navigation */
    .categories-sidebar {
        border-radius: 24px !important;
    }

    .border-bottom-sidebar {
        border-bottom: 1px solid var(--border-color);
    }

    .sidebar-title {
        color: var(--text-primary);
        font-size: 1.15rem;
        letter-spacing: -0.3px;
    }

    .btn-reset-sidebar {
        background: transparent;
        border: none;
        transition: color 0.2s ease;
        cursor: pointer;
    }

    .btn-reset-sidebar:hover {
        color: #dc3545 !important;
    }

    .sidebar-cat-item {
        background-color: transparent;
        color: var(--text-secondary);
        border: 1px solid transparent;
        border-radius: 14px;
        padding: 11px 16px;
        font-size: 0.95rem;
        font-weight: 600;
        transition: all 0.25s ease;
        display: flex;
        align-items: center;
        width: 100%;
        cursor: pointer;
        outline: none !important;
    }

    .sidebar-cat-item:hover {
        color: var(--text-primary);
        background-color: rgba(145, 216, 247, 0.1);
        transform: translateX(4px);
    }

    .sidebar-cat-item.active {
        background-color: var(--accent-color);
        color: #ffffff;
        border-color: var(--accent-color);
        box-shadow: 0 4px 14px rgba(145, 216, 247, 0.35);
    }

    :root[data-theme="dark"] .sidebar-cat-item.active {
        color: #0f172a;
    }

    .cat-icon {
        font-size: 0.9em;
        opacity: 0.85;
    }

    .badge-search-info {
        background-color: var(--surface-color);
        color: var(--text-primary);
        border: 1px solid var(--border-color);
        font-size: 0.82rem;
    }

    /* Loading Overlay */
    .min-h-400 {
        min-height: 400px;
    }

    .loading-overlay {
        top: 0;
        left: 0;
        background-color: rgba(255, 255, 255, 0.65);
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(4px);
        z-index: 100;
        border-radius: 20px;
        transition: opacity 0.3s ease;
    }

    :root[data-theme="dark"] .loading-overlay {
        background-color: rgba(26, 32, 44, 0.75);
    }

    .spinner-box {
        border: 1px solid var(--border-color);
    }

    /* Custom Pagination */
    .custom-pagination .page-item .page-link {
        color: var(--text-primary);
        background-color: var(--surface-color);
        border: 1px solid var(--border-color);
        margin: 0 5px;
        margin-bottom: 8px;
        border-radius: 14px;
        transition: all 0.25s ease;
        font-weight: 700;
        width: 46px;
        height: 46px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
        cursor: pointer;
        outline: none !important;
    }
    
    .custom-pagination .page-item:not(.disabled) .page-link:hover {
        background-color: var(--accent-color);
        color: #ffffff;
        border-color: var(--accent-color);
        transform: translateY(-3px);
        box-shadow: 0 6px 16px rgba(145, 216, 247, 0.4);
    }

    :root[data-theme="dark"] .custom-pagination .page-item:not(.disabled) .page-link:hover {
        color: #0f172a;
    }

    .custom-pagination .page-item.active .page-link {
        background-color: var(--accent-color);
        color: #ffffff;
        border-color: var(--accent-color);
        box-shadow: 0 6px 16px rgba(145, 216, 247, 0.4);
    }

    :root[data-theme="dark"] .custom-pagination .page-item.active .page-link {
        color: #0f172a;
    }

    .custom-pagination .page-item.disabled .page-link {
        opacity: 0.4;
        cursor: not-allowed;
        background-color: transparent;
        box-shadow: none;
    }

    @media (max-width: 768px) {
        .hero-title {
            font-size: 2.1rem;
        }
        .hero-subtitle {
            font-size: 0.98rem;
        }
    }
</style>